import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Camera, RefreshCw, Download } from 'lucide-react';
import { Button, Card, Pill } from '@nako/ui';
import { formatMoney, getRegion } from '@nako/core';
import { useAuthStore } from '@/stores/auth';
import { useRegionStore } from '@/stores/region';
import { useReceiptsStore } from '@/stores/receipts';
import { useUiStore } from '@/stores/ui';
import { makeT } from '@/lib/i18n';

function q(v: string | number | boolean | null | undefined): string {
  const s = v == null ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

const STATUS_TONE: Record<string, 'green' | 'red' | 'neutral' | 'accent' | 'blue'> = {
  completed: 'green',
  failed: 'red',
  manual: 'accent',
  pending: 'neutral',
  processing: 'blue',
};

export default function Receipts() {
  const business = useAuthStore((s) => s.business)!;
  const lang = useUiStore((s) => s.lang);
  const regionCode = useRegionStore((s) => s.code);
  const t = makeT(lang);
  const { receipts, queued, refresh, flushQueue } = useReceiptsStore();
  const [filter, setFilter] = useState<'all' | 'completed' | 'failed' | 'pending'>('all');
  const [syncing, setSyncing] = useState(false);
  const failedCount = queued.filter((q) => q.sync_status === 'failed').length;

  const handleSync = async () => {
    setSyncing(true);
    try {
      await flushQueue();
      await refresh(business.id);
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    void refresh(business.id);
  }, [business.id, refresh]);

  const filtered = receipts.filter((r) => filter === 'all' || r.ocr_status === filter);

  return (
    <main className="mx-auto max-w-3xl px-4 py-4 md:px-6 md:py-6">
      <div className="mb-4 flex items-center justify-between gap-2">
        <h1 className="font-display text-2xl">{t('receipts.title')}</h1>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="secondary"
            leftIcon={<Download size={14} />}
            onClick={() => {
              const ccy = getRegion(regionCode).currencyCode;
              const lines: string[] = [];
              lines.push(`Receipts,,,${ccy}`);
              lines.push('Date,Merchant,Total,Status,OCR Tier');
              filtered.forEach((r) =>
                lines.push(
                  `${q(r.receipt_date ?? r.uploaded_at?.slice(0, 10))},${q(r.merchant_name ?? '')},${
                    r.total_amount ?? ''
                  },${q(r.ocr_status)},${r.ocr_tier_used ?? ''}`,
                ),
              );
              const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `receipts-${business.name}-${new Date().toISOString().slice(0, 10)}.csv`;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              setTimeout(() => URL.revokeObjectURL(url), 1000);
            }}
            disabled={filtered.length === 0}
          >
            CSV
          </Button>
          <Link to="/app/receipts/scan">
            <Button leftIcon={<Camera size={16} />} size="sm">
              Scan
            </Button>
          </Link>
        </div>
      </div>

      {queued.length > 0 && (
        <Card
          className={`mb-4 ${failedCount > 0 ? 'bg-red/10 border-red/30' : 'bg-blue/10 border-blue/30'}`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm font-medium ${failedCount > 0 ? 'text-red' : 'text-blue'}`}>
                📤 {queued.length} receipt{queued.length === 1 ? '' : 's'} waiting to sync
              </p>
              {failedCount > 0 && (
                <p className="text-muted mt-0.5 text-xs">
                  {failedCount} failed — tap Retry to try again
                </p>
              )}
            </div>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => void handleSync()}
              loading={syncing}
              leftIcon={<RefreshCw size={14} />}
            >
              {failedCount > 0 ? 'Retry' : 'Sync now'}
            </Button>
          </div>
        </Card>
      )}

      <div className="-mx-4 mb-4 flex gap-2 overflow-x-auto px-4 md:mx-0 md:px-0">
        {(['all', 'completed', 'pending', 'failed'] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`rounded-pill whitespace-nowrap border px-3 py-1.5 text-xs font-medium ${
              filter === f ? 'bg-ink text-paper border-ink' : 'text-muted border-line bg-white'
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <Card className="py-12 text-center">
          <p className="text-muted">{t('receipts.empty')}</p>
          <Link to="/app/receipts/scan" className="mt-4 inline-block">
            <Button leftIcon={<Camera size={18} />}>{t('receipts.take_photo')}</Button>
          </Link>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map((r) => (
            <Card key={r.id} compact className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">{r.merchant_name ?? 'Unknown merchant'}</p>
                <p className="text-muted text-xs">
                  {r.receipt_date ?? r.uploaded_at?.slice(0, 10)}
                  {r.ocr_tier_used != null && ` · tier ${r.ocr_tier_used}`}
                </p>
              </div>
              <div className="ml-3 text-right">
                <p className="font-mono text-sm">
                  {r.total_amount != null ? formatMoney(r.total_amount, regionCode) : '—'}
                </p>
                <Pill tone={STATUS_TONE[r.ocr_status] ?? 'neutral'} className="mt-1">
                  {r.ocr_status}
                </Pill>
              </div>
            </Card>
          ))}
        </div>
      )}
    </main>
  );
}
